import { FC } from 'react';
import { FormProvider } from 'react-hook-form';
import { useParams } from 'react-router-dom';
import { Col } from 'react-styled-flexboxgrid';

import { BUTTON_TYPES } from '@savgroup-front-common/constants';
import { Button } from '@savgroup-front-common/core/atoms/button';
import { SafeFormattedMessageWithoutSpread } from '@savgroup-front-common/core/formatters';
import { SUPPORTED_ICON_POSITIONS } from '@savgroup-front-common/core/helpers';
import { RoutedStepProps } from '@savgroup-front-common/core/molecules/RoutedStepsOrchestrator/RoutedStepsOrchestrator.types';
import ArrowNakedLeftIcon from '@savgroup-front-common/core/protons/icons/ArrowNaked/ArrowNakedLeft.icon';
import { StartOrContinueDiagnosticTreeQueryReturnValues } from '@/api/Claim/startOrContinueDiagnosticTreeQuery';

import { STEP_CONFIG } from '../../../app/NewLayout/ClassiqueRoutes.config';
import {
  NewStepLayout,
  PageFooterContainer,
} from '../../../app/NewLayout/NewStepLayout';
import { IrshStepValues } from '../IrshPages.types';

import StepAnswers from './components/stepAnswers/StepAnswers';
import StepFinal from './components/stepFinal/StepFinal';
import StepInformations from './components/stepInformations/StepInformations';
import StepNumber from './components/stepNumber/StepNumber';
import messages from './messages';
import useClaimGroupDiagnosticTreePage from './NewClaimGroupDiagnosticTreePageContent.hooks';
import { $RowContent } from './NewClaimGroupDiagnosticTreePageContent.styles';
import { DIAGNOSTIC_STATE_TYPE } from './NewClaimGroupDiagnosticTreePageContent.types';

export const NewClaimGroupDiagnosticTreePageContent: FC<
  RoutedStepProps<IrshStepValues> & {
    startOrContinueDiagnosticTreeQueryReturnValues: StartOrContinueDiagnosticTreeQueryReturnValues;
  }
> = ({
  onPreviousStep,
  onNextStep,
  startOrContinueDiagnosticTreeQueryReturnValues,
}) => {
  const { questionId: diagnosticTreeUniqueName } = useParams() as {
    questionId: string;
  };

  const { formContext, handleSubmitResponse, handlePreviousStep } =
    useClaimGroupDiagnosticTreePage({
      diagnosticTreeUniqueName,
      onPreviousStep,
      startOrContinueDiagnosticTreeQueryReturnValues,
    });

  const diagnosticTreeState = startOrContinueDiagnosticTreeQueryReturnValues;

  const hasQuestionType =
    diagnosticTreeState?.currentNode?.type === DIAGNOSTIC_STATE_TYPE.QUESTION ||
    diagnosticTreeState?.currentNode?.type === DIAGNOSTIC_STATE_TYPE.ACTION;

  const stepNumberLength = diagnosticTreeState.history.length + 1;

  return (
    <NewStepLayout stepName={STEP_CONFIG.CLAIM_GROUP_DIAGNOSTIC_TREE}>
      {hasQuestionType && (
        <FormProvider {...formContext}>
          <form
            onSubmit={(event) => {
              event.preventDefault();

              return undefined;
            }}
          >
            <$RowContent $stepNumberLength={stepNumberLength}>
              <StepNumber currentDiagnosticTreeStep={diagnosticTreeState} />

              <Col xs={12} sm={12} md={9}>
                <StepInformations
                  currentDiagnosticTreeStep={diagnosticTreeState}
                />

                <StepAnswers
                  currentDiagnosticTreeStep={diagnosticTreeState}
                  onSubmit={handleSubmitResponse as any}
                />
              </Col>
            </$RowContent>

            <PageFooterContainer reverseChildren={false}>
              <Button
                type={BUTTON_TYPES.BUTTON}
                tertiary
                onClick={() => handlePreviousStep()}
                icon={<ArrowNakedLeftIcon />}
                position={SUPPORTED_ICON_POSITIONS.LEFT}
                dataTestId="previousButton"
              >
                <SafeFormattedMessageWithoutSpread
                  message={messages.previousButton}
                />
              </Button>
            </PageFooterContainer>
          </form>
        </FormProvider>
      )}

      {diagnosticTreeState?.currentNode?.type ===
        DIAGNOSTIC_STATE_TYPE.FINAL_STATE && (
        <StepFinal
          currentDiagnosticTreeStep={diagnosticTreeState}
          onNextStep={onNextStep}
        />
      )}
    </NewStepLayout>
  );
};
